import { z } from "zod";
import { isValidCron } from "./cron.js";

/**
 * Wire schemas: everything that crosses a process boundary is validated with these.
 * Changing a schema here is a contract change for the SDK, cloud, apps and MCP.
 */

export const Level = z.enum(["wake", "tell", "log"]);
export type Level = z.infer<typeof Level>;

/** Signals the AI may never demote. */
export const FloorKind = z.enum(["app_silent", "crash_loop", "http_5xx", "payments_failing"]);
export type FloorKind = z.infer<typeof FloorKind>;

/**
 * "resolve" (additive): the source says the thing behind `fingerprint` is healthy again (a CloudWatch
 * alarm back to OK). Core closes the open problem for that fingerprint; without one it is ignored.
 * "checkin" (additive): a scheduled job reporting a run; the event carries `checkin`.
 */
export const EventType = z.enum(["error", "metric", "heartbeat", "deploy", "custom", "resolve", "checkin"]);
export type EventType = z.infer<typeof EventType>;

/** IANA time zone the runtime knows, e.g. "Asia/Kolkata". Validated with Intl, so typos are rejected. */
export const TimeZone = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: "unknown time zone" },
  );
export type TimeZone = z.infer<typeof TimeZone>;

// ───────────── scheduled-job check-ins ─────────────

/** A job's name in URLs and SDK calls: "nightly-backup". Lowercased; letters, digits, `.`, `_`, `-`. */
export const CheckInSlug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9._-]{0,63}$/, "use 1-64 letters, digits, '.', '_' or '-', starting with a letter or digit");
export type CheckInSlug = z.infer<typeof CheckInSlug>;

export const CheckInStatus = z.enum(["ok", "failed"]);
export type CheckInStatus = z.infer<typeof CheckInStatus>;

/** How loud a missed or failed run is. Never "log": a job nobody would hear about isn't watched. */
export const CheckInLevel = z.enum(["tell", "wake"]);
export type CheckInLevel = z.infer<typeof CheckInLevel>;

/** Five-field cron ("30 3 * * *") or a macro (@daily). Must fire at least once. */
export const CronExpr = z.string().trim().min(1).max(100).refine(isValidCron, {
  message: "not a cron expression that ever runs (use 5 fields: minute hour day month weekday)",
});

/** Longest gap a check-in may promise: a bit over a year. */
export const CHECKIN_MAX_EVERY_S = 400 * 86_400;

/**
 * When a job should report, as the SDK declares it or a person sets it in the web app. Exactly one of
 * `expectEverySeconds` (elapsed time since the last run) or `cron` (wall-clock times in `tz`; default the
 * account's zone, else UTC). `graceSeconds` is how late a run may be before it counts as missed (default:
 * a tenth of the gap, 5 minutes to 1 hour). `level` default "tell".
 */
const CheckInScheduleFields = z.object({
  /** Plain-words name for alerts ("Nightly backup"); default from the slug. */
  name: z.string().trim().min(1).max(100).optional(),
  expectEverySeconds: z.number().int().min(60).max(CHECKIN_MAX_EVERY_S).optional(),
  cron: CronExpr.optional(),
  tz: TimeZone.optional(),
  graceSeconds: z
    .number()
    .int()
    .min(0)
    .max(7 * 86_400)
    .optional(),
  level: CheckInLevel.optional(),
});
const oneSchedule = {
  check: (s: { expectEverySeconds?: number; cron?: string }) =>
    (s.expectEverySeconds === undefined) !== (s.cron === undefined),
  message: "give exactly one of expectEverySeconds or cron",
};
export const CheckInSchedule = CheckInScheduleFields.refine(oneSchedule.check, { message: oneSchedule.message });
export type CheckInSchedule = z.infer<typeof CheckInSchedule>;

/**
 * PUT /v1/projects/:id/checkins/:slug (web app). Once a person edits a check-in, the schedule the SDK
 * sends no longer changes it. `paused` stops miss alerts; a failed run is still reported.
 */
export const CheckInEdit = CheckInScheduleFields.extend({ paused: z.boolean().optional() }).refine(oneSchedule.check, {
  message: oneSchedule.message,
});
export type CheckInEdit = z.infer<typeof CheckInEdit>;

/** What a check-in event carries (IngestEvent type "checkin"). */
export const CheckInPayload = z.object({
  slug: CheckInSlug,
  status: CheckInStatus.default("ok"),
  /** How long the run took. */
  durationMs: z
    .number()
    .int()
    .nonnegative()
    .max(CHECKIN_MAX_EVERY_S * 1000)
    .optional(),
  /** Why it failed, in the job's words. Untrusted: only ever displayed. */
  message: z.string().max(500).optional(),
  /** Creates the check-in on first use and keeps it in step, unless a person edited it in the web app. */
  schedule: CheckInSchedule.optional(),
});
export type CheckInPayload = z.infer<typeof CheckInPayload>;

/** POST /v1/checkins/:slug on ingest. An empty body is `{status: "ok"}`. */
export const CheckInBody = z.object({
  status: CheckInStatus.default("ok"),
  durationMs: CheckInPayload.shape.durationMs,
  message: CheckInPayload.shape.message,
  schedule: CheckInSchedule.optional(),
  /** Idempotency key; a retry with the same id counts once. */
  id: z.string().min(8).max(64).optional(),
  env: z.string().min(1).max(40).optional(),
});
export type CheckInBody = z.infer<typeof CheckInBody>;

/** One event as sent by pagerless/auto or the JSON event API. `id` is the idempotency key. */
export const IngestEvent = z.object({
  v: z.literal(1),
  id: z.string().min(8).max(64),
  type: EventType,
  ts: z.number().int().nonnegative(),
  service: z.string().min(1).max(100),
  env: z.string().min(1).max(40).default("production"),
  release: z.string().max(100).optional(),
  /** Stable grouping key computed in the SDK (error type + top frames, route). */
  fingerprint: z.string().max(128).optional(),
  route: z.string().max(300).optional(),
  /** Pre-aggregated count in the SDK window. */
  count: z.number().int().positive().default(1),
  window_s: z.number().int().positive().max(3600).optional(),
  status: z.number().int().min(100).max(599).optional(),
  error: z
    .object({
      type: z.string().max(200),
      message: z.string().max(2000),
      stack: z.string().max(16000).optional(),
    })
    .optional(),
  metric: z.object({ name: z.string().max(100), value: z.number(), unit: z.string().max(20).optional() }).optional(),
  tags: z.record(z.string().max(64), z.string().max(256)).optional(),
  /** Only for type "custom": the caller says how loud it is. */
  severity: Level.optional(),
  title: z.string().max(200).optional(),
  /** Only for type "checkin". */
  checkin: CheckInPayload.optional(),
});
export type IngestEvent = z.infer<typeof IngestEvent>;

export const IngestBatch = z.object({ events: z.array(IngestEvent).min(1).max(500) });
export type IngestBatch = z.infer<typeof IngestBatch>;

/** What ingest puts on the queue. One message per event. */
export const QueueMessage = z.object({
  accountId: z.string(),
  projectId: z.string(),
  receivedAt: z.number().int(),
  event: IngestEvent,
});
export type QueueMessage = z.infer<typeof QueueMessage>;

export const ProblemState = z.enum(["open", "acked", "fixing", "resolved"]);
export type ProblemState = z.infer<typeof ProblemState>;

export const DecidedBy = z.enum(["floor", "baseline", "jev", "claude", "haiku", "rules", "human"]);
export type DecidedBy = z.infer<typeof DecidedBy>;

export const Decision = z.object({
  level: Level,
  confidence: z.number().min(0).max(1),
  decidedBy: DecidedBy,
  reason: z.string().max(500),
  floor: FloorKind.nullable(),
});
export type Decision = z.infer<typeof Decision>;

export const ActionKind = z.enum([
  "undo_update",
  "restart",
  "add_capacity",
  "pause",
  "open_pr",
  "email_customers",
  "data_change",
  "custom",
]);
export type ActionKind = z.infer<typeof ActionKind>;

/** Action kinds that are "never" regardless of any rule or approval. */
export const ALWAYS_NEVER: readonly ActionKind[] = ["data_change"] as const;

export const PolicyMode = z.enum(["alone", "ask", "never"]);
export type PolicyMode = z.infer<typeof PolicyMode>;

export const PolicyRule = z.object({
  kind: ActionKind,
  mode: PolicyMode,
  /** Narrows the rule, e.g. "undo_update alone only when payments broke". */
  when: z
    .object({
      floor: z.array(FloorKind).optional(),
      afterDeployWithinS: z.number().int().positive().optional(),
      maxCapacityFactor: z.number().positive().max(10).optional(),
    })
    .optional(),
});
export type PolicyRule = z.infer<typeof PolicyRule>;

export const PolicyPreset = z.enum(["careful", "balanced", "handsoff"]);
export type PolicyPreset = z.infer<typeof PolicyPreset>;

export const ActionTarget = z.object({
  hostId: z.string().max(40),
  connectionId: z.string().max(64),
  resource: z.string().max(300),
});
export type ActionTarget = z.infer<typeof ActionTarget>;

export const ActionState = z.enum([
  "proposed",
  "awaiting_approval",
  "approved",
  "rejected",
  "refused",
  "running",
  "verifying",
  "done",
  "failed",
  "reverted",
]);
export type ActionState = z.infer<typeof ActionState>;

export const Approval = z.object({
  personId: z.string(),
  at: z.number().int(),
  method: z.enum(["passkey", "tap"]),
});
export type Approval = z.infer<typeof Approval>;

export const Action = z.object({
  id: z.string(),
  problemId: z.string(),
  kind: ActionKind,
  /** Plain-words description for humans: "Undo the 3:08 update". */
  title: z.string().max(200),
  params: z.record(z.string(), z.unknown()).default({}),
  target: ActionTarget.nullable(),
  proposedBy: z.enum(["helper", "agent", "human"]),
  policy: PolicyMode,
  /** Plain-words reason for the policy verdict, from `evaluatePolicy`. */
  policyReason: z.string().max(300).optional(),
  state: ActionState,
  reversible: z.boolean(),
  approval: Approval.nullable(),
  result: z.object({ ok: z.boolean(), detail: z.string().max(2000) }).nullable(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Action = z.infer<typeof Action>;

export const Diagnosis = z.object({
  /** Plain words: "4 in 10 people who try to pay see an error." */
  what: z.string().max(500),
  why: z.string().max(1000),
  plan: z.array(z.object({ kind: ActionKind, title: z.string().max(200), params: z.record(z.string(), z.unknown()) })),
  confidence: z.number().min(0).max(1),
  model: z.string().max(60),
  /**
   * Set by core, not the helper: plain-words steps for plan items Pagerless can't do itself (no host
   * connection, or the host has no API), including text the customer can hand to their own agent.
   */
  guide: z.string().max(4000).optional(),
});
export type Diagnosis = z.infer<typeof Diagnosis>;

export const Problem = z.object({
  id: z.string(),
  projectId: z.string(),
  key: z.string().max(200),
  title: z.string().max(200),
  state: ProblemState,
  decision: Decision,
  diagnosis: Diagnosis.nullable(),
  eventCount: z.number().int().nonnegative(),
  openedAt: z.number().int(),
  lastSeenAt: z.number().int(),
  ackedAt: z.number().int().nullable(),
  ackedBy: z.string().nullable(),
  resolvedAt: z.number().int().nullable(),
  summary: z.string().max(2000).nullable(),
  /**
   * When a person first acked it or approved a fix for it (epoch ms); survives reopen, which clears
   * the ack. Non-null means "needed you"; resolved with null means "fixed by your helper" (bug #18).
   * Additive: optional so older servers' problems still parse.
   */
  firstHumanAt: z.number().int().nullable().optional(),
  /**
   * Additive: the service the problem came from (the first event's `service`; "job:<slug>" for a
   * scheduled job). Optional so older servers' problems still parse; null when not known.
   */
  service: z.string().max(100).nullable().optional(),
});
export type Problem = z.infer<typeof Problem>;

export const Person = z.object({
  id: z.string(),
  name: z.string().max(100),
  email: z.string().email().max(254),
  role: z.enum(["owner", "member"]),
  /** Escalation order: 0 first, then 1 after the backup delay. */
  order: z.number().int().min(0).max(10),
  phoneE164: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/)
    .nullable(),
});
export type Person = z.infer<typeof Person>;

export const ChannelKind = z.enum(["push_ios", "push_android", "web", "slack", "telegram", "email", "webhook", "call"]);
export type ChannelKind = z.infer<typeof ChannelKind>;

/**
 * Channel kinds cloud/deliver can actually send. Escalate skips the rest (neither sent nor recorded
 * as a failure). `web` is listed in ChannelKind for the dashboard, but there is no web-push adapter yet.
 */
export const DELIVERABLE_CHANNEL_KINDS: readonly ChannelKind[] = [
  "push_ios",
  "push_android",
  "slack",
  "telegram",
  "email",
  "webhook",
  "call",
] as const;
export const isDeliverableKind = (k: ChannelKind): boolean => DELIVERABLE_CHANNEL_KINDS.includes(k);

export const Channel = z.object({
  id: z.string(),
  kind: ChannelKind,
  personId: z.string().nullable(),
  /** Adapter-specific config. Secrets are references to the secret store, never raw values. */
  config: z.record(z.string(), z.string()),
  levels: z.array(Level),
  lastTestAt: z.number().int().nullable(),
  lastTestOk: z.boolean().nullable(),
});
export type Channel = z.infer<typeof Channel>;

export const AuditEntry = z.object({
  seq: z.number().int().nonnegative(),
  at: z.number().int(),
  actor: z.string().max(100),
  kind: z.string().max(60),
  data: z.record(z.string(), z.unknown()),
  prevHash: z.string().length(64),
  hash: z.string().length(64),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

/** Weekly report counts (the LCD screen: fixed by helper, needed you, seconds of your time). */
export const WeekStats = z.object({
  fromMs: z.number().int(),
  toMs: z.number().int(),
  problems: z.number().int().nonnegative(),
  /** Resolved by the helper (alone, or approved then verified) with no human ack. */
  fixedByHelper: z.number().int().nonnegative(),
  /** Level "tell": a message, no call. */
  told: z.number().int().nonnegative(),
  /** A human acked, approved or took over. */
  neededYou: z.number().int().nonnegative(),
  /** Seconds a human spent: first delivery to ack or approval, summed. */
  humanSeconds: z.number().int().nonnegative(),
});
export type WeekStats = z.infer<typeof WeekStats>;

/**
 * A passkey registered for a person (by cloud/api after a WebAuthn registration ceremony).
 * `publicKey` is the credential's COSE public key, base64url, exactly as WebAuthn registration returns it.
 */
export const PasskeyCredential = z.object({
  id: z.string().min(1).max(1024),
  personId: z.string(),
  publicKey: z.string().min(1).max(4096),
  counter: z.number().int().nonnegative(),
  transports: z.array(z.string().max(32)).max(10).optional(),
  createdAt: z.number().int(),
});
export type PasskeyCredential = z.infer<typeof PasskeyCredential>;

/**
 * A project's connection to its host (Vercel, Railway, fake-host, ...). Core owns these; the access
 * token lives in AccountDO's secret store and only its reference is kept here.
 */
export const HostConnection = z.object({
  id: z.string().regex(/^con_[A-Za-z0-9_-]{1,60}$/),
  projectId: z.string().min(1).max(64),
  hostId: z.string().min(1).max(40),
  /** The host's id for the app, e.g. a Vercel project or a Neon "project/branch". */
  resource: z.string().min(1).max(300),
  /** `secret:<name>:<scope>` from AccountSecretsApi.putSecret. */
  secretRef: z.string().regex(/^secret:[a-z0-9_]{1,64}:[A-Za-z0-9_-]{1,64}$/),
  createdAt: z.number().int(),
});
export type HostConnection = z.infer<typeof HostConnection>;

/** "HH:MM", 24-hour clock. */
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export type ClockTime = z.infer<typeof ClockTime>;

/** Account-wide quiet hours: "tell" waits until they end; "wake" always goes through. */
export const QuietHours = z.object({
  start: ClockTime,
  end: ClockTime,
  /** IANA time zone, e.g. "Europe/London". */
  tz: z.string().min(1).max(64),
});
export type QuietHours = z.infer<typeof QuietHours>;

/**
 * Account settings (AccountDO.getSettings, GET /v1/settings). `timeZone` is the zone times are shown in
 * ("the 10:02 update"); null when none is known yet (then text says "the latest update").
 */
export const AccountSettings = z.object({ timeZone: TimeZone.nullable() });
export type AccountSettings = z.infer<typeof AccountSettings>;

/** PUT /v1/settings body: set the account's time zone. */
export const AccountSettingsBody = z.object({ timeZone: TimeZone });
export type AccountSettingsBody = z.infer<typeof AccountSettingsBody>;

/**
 * Account alert settings (AccountDO, GET/PUT /v1/alerts). Channel config `quietStart`/`quietEnd`/`tz`
 * still overrides `quietHours` for that one channel. Floor signals always wake, whatever `wakeFor` says.
 */
export const AlertSettings = z.object({
  quietHours: QuietHours.nullable(),
  /** Plain-words list of what wakes someone up ("Payments down"). */
  wakeFor: z.array(z.string().min(1).max(100)).max(20),
  morningSummary: z.object({ at: ClockTime, channelKind: ChannelKind }).nullable(),
});
export type AlertSettings = z.infer<typeof AlertSettings>;

/**
 * Heartbeat health per service (AccountDO.heartbeatStatus). `online`: a heartbeat arrived within the
 * silence window and no "app silent" problem is open. `upSince`: the later of the first heartbeat and
 * the end of the last outage (an "app silent" problem), null while it is down; `upDays` counts whole
 * days since then.
 */
export const HeartbeatStatus = z.object({
  projectId: z.string(),
  service: z.string().max(100),
  env: z.string().max(40),
  lastAt: z.number().int(),
  firstAt: z.number().int(),
  armed: z.boolean(),
  droppedTotal: z.number().int().nonnegative(),
  online: z.boolean(),
  upSince: z.number().int().nullable(),
  upDays: z.number().int().nonnegative().nullable(),
});
export type HeartbeatStatus = z.infer<typeof HeartbeatStatus>;
